import { Box, VStack } from "@chakra-ui/react";
import { Alert, Button, Divider } from "antd";
import { StaffRoutes } from "appRoutePaths";
import axios from "axios";
import CreatedPackageDetail from "components/staff/Packages/CreatedPackageDetail";
import NewPackageForm, {
    NewPackageFormData,
} from "components/staff/Packages/NewPackageForm";
import useCreatePackage from "hooks/useCreatePackage";
import { ApplicationUser } from "models/applicationUsers";
import { PackageAmount, PackageType, StaffPackage } from "models/packages";
import { useState } from "react";
import { Link } from "react-router-dom";

interface StickyData {
    packageType: PackageType;
    customer?: ApplicationUser;
    amount?: PackageAmount;
    trackingNumberSuffix?: string;
}

// const PackageTypeIdHECB = "58ed1724-0004-4f19-ac2c-a533d7ab240e"; // staging: "Test"
const PackageTypeIdHECB = "52af57c8-0ca6-4519-adc1-bc0fcdeb2241"; // prod: "HECB"

export const PackageQRCodeLink = ({ id, text = "QR Code" }: { id: string, text?: string }) =>
    <Button size="large" type="link">
        <Link to={StaffRoutes.packageQRCode.url(id)} target="_blank" rel="noreferrer">{text}</Link>
    </Button>;

const NewPackage = () => {
    const [createdPackage, setCreatedPackage] = useState<StaffPackage>();
    const [prevPackage, setPrevPackage] = useState<StaffPackage>();
    const { createPackage, working } = useCreatePackage();
    const [stickyData, setStickyData] = useState<StickyData>();
    const [error, setError] = useState<string>();

    const submitForm = async (formData: NewPackageFormData) => {
        setError(undefined);

        try {
            const data = await createPackage(formData);
            setCreatedPackage(data);

            const shouldStick =
                formData.packageType.supportSilentAccounts ||
                formData.packageType.id === PackageTypeIdHECB;

            if (shouldStick) {
                const { owners, ...rest } = formData;
                setStickyData({
                    ...rest,
                    customer: owners.customers[0],
                });
            } else {
                setStickyData(undefined);
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const problem = error.response!.data as any;
                setError(problem!.title);
            }
        }
    };

    const resetPage = () => {
        setPrevPackage(createdPackage);
        setCreatedPackage(undefined);
    };

    return (
        <VStack p={50} align="start" fontSize="1.6rem">
            <Box fontSize={36}>Add New Package</Box>
            <Divider />
            <Box fontSize={26}>Package Information</Box>
            {createdPackage ? (
                <CreatedPackageDetail
                    prevPackage={prevPackage}
                    package={createdPackage}
                    onAddNext={resetPage}
                />
            ) : (
                <NewPackageForm
                    prevPackage={prevPackage}
                    disabled={working}
                    {...stickyData}
                    onSubmit={submitForm}
                />
            )}

            <div style={{ maxWidth: '30rem' }}>
                {error && <Alert
                    type="error"
                    showIcon
                    description={error}
                />}
            </div>
        </VStack>
    );
};

export default NewPackage;
